import type { Screen } from '@ghostscreen/contracts';
import { i18n } from '../../../i18n';
import { formatRelativeTime } from '../relativeTime';
import { getCardConnectionStatus, type CardConnectionStatus } from '../screenStatus';

export type OverviewTone = 'ok' | 'warn' | 'bad' | 'muted' | 'accent';

export type OverviewIssue = {
  screenId: string;
  name: string;
  reason: string;
  /** Button label, e.g. "Fix it". */
  action: string;
  tone: OverviewTone;
};

export type OverviewStatusModel = {
  greeting: string;
  headline: string;
  headTone: OverviewTone;
  subline: string;
  issues: OverviewIssue[];
  issueCountLabel: string;
};

export type OverviewScreenRow = {
  screen: Screen;
  connection: CardConnectionStatus;
  statusLabel: string;
  showingLabel: string;
  playlistName: string | null;
  hasContent: boolean;
  sortRank: number;
};

const CONNECTION_SORT: Record<CardConnectionStatus, number> = {
  online: 0,
  offline: 1,
  unknown: 1,
  unpaired: 2,
};

const TONE_CSS: Record<OverviewTone, string> = {
  ok: 'var(--gs-ok)',
  warn: 'var(--gs-warn)',
  bad: 'var(--gs-bad)',
  muted: 'var(--gs-muted)',
  accent: 'var(--gs-accent)',
};

/** CSS color token for overview status dots / headlines. */
export function overviewToneColor(tone: OverviewTone): string {
  return TONE_CSS[tone];
}

export function greetingForHour(hour: number): string {
  if (hour < 12) return i18n.t('overview:greeting.morning');
  if (hour < 18) return i18n.t('overview:greeting.afternoon');
  return i18n.t('overview:greeting.evening');
}

/**
 * A paired online screen is "showing something" when a playlist is assigned
 * and a published version is desired — the same signal the player syncs on.
 */
export function screenHasSomethingToShow(
  connection: CardConnectionStatus,
  playlistId: string | null | undefined,
  desiredPlaylistVersion: number | null | undefined,
): boolean {
  if (connection !== 'online') return false;
  if (!playlistId) return false;
  return typeof desiredPlaylistVersion === 'number' && desiredPlaylistVersion > 0;
}

export function statusLabelForConnection(connection: CardConnectionStatus): string {
  return i18n.t(`overview:connection.${connection}`);
}

export function showingLabelForScreen(input: {
  connection: CardConnectionStatus;
  playlistName: string | null;
  hasContent: boolean;
}): string {
  if (input.connection === 'unpaired') return i18n.t('overview:showing.waitingSetup');
  if (!input.playlistName || !input.hasContent) return i18n.t('overview:showing.nothing');
  return i18n.t('overview:showing.playlist', { name: input.playlistName });
}

function issueForScreen(
  screen: Screen,
  connection: CardConnectionStatus,
  playlistId: string | null | undefined,
  now: number,
): OverviewIssue | null {
  if (connection === 'offline') {
    const seen = formatRelativeTime(screen.lastHeartbeatAt, now).toLowerCase();
    return {
      screenId: screen.id,
      name: screen.name,
      reason: i18n.t('overview:issues.offlineReason', { seen }),
      action: i18n.t('overview:issues.fixIt'),
      tone: 'bad',
    };
  }

  if (connection === 'unpaired') {
    return {
      screenId: screen.id,
      name: screen.name,
      reason: i18n.t('overview:issues.unpairedReason'),
      action: i18n.t('overview:issues.setItUp'),
      tone: 'muted',
    };
  }

  if (connection === 'unknown') {
    return {
      screenId: screen.id,
      name: screen.name,
      reason: i18n.t('overview:issues.unknownReason'),
      action: i18n.t('overview:issues.checkIt'),
      tone: 'warn',
    };
  }

  if (!screenHasSomethingToShow(connection, playlistId, screen.desiredPlaylistVersion)) {
    return {
      screenId: screen.id,
      name: screen.name,
      reason: i18n.t('overview:issues.blankReason'),
      action: i18n.t('overview:issues.pickSomething'),
      tone: 'warn',
    };
  }

  return null;
}

/**
 * Pure overview status model — greeting, headline, attention list.
 * Assignment map is screenId → playlistId (null when none).
 */
export function buildOverviewStatus(
  screens: Screen[],
  assignments: ReadonlyMap<string, string | null>,
  options: { now?: Date } = {},
): OverviewStatusModel {
  const nowDate = options.now ?? new Date();
  const now = nowDate.getTime();
  const hour = nowDate.getHours();

  const issues: OverviewIssue[] = [];
  let showingCount = 0;

  for (const screen of screens) {
    const connection = getCardConnectionStatus(screen);
    const playlistId = assignments.get(screen.id) ?? null;
    const issue = issueForScreen(screen, connection, playlistId, now);
    if (issue) issues.push(issue);
    if (screenHasSomethingToShow(connection, playlistId, screen.desiredPlaylistVersion)) {
      showingCount += 1;
    }
  }

  const n = issues.length;
  const greeting = greetingForHour(hour);

  if (screens.length === 0) {
    return {
      greeting,
      headline: i18n.t('overview:status.emptyHeadline'),
      headTone: 'muted',
      subline: i18n.t('overview:status.emptySubline'),
      issues: [],
      issueCountLabel: i18n.t('overview:status.issueCount', { count: 0 }),
    };
  }

  const hasOffline = issues.some((issue) => issue.tone === 'bad');
  const headline = n
    ? i18n.t('overview:status.headline', { count: n })
    : i18n.t('overview:status.allRunning');
  const headTone: OverviewTone = hasOffline ? 'bad' : n ? 'warn' : 'ok';

  return {
    greeting,
    headline,
    headTone,
    subline: i18n.t('overview:status.subline', {
      count: screens.length,
      showing: showingCount,
    }),
    issues,
    issueCountLabel: i18n.t('overview:status.issueCount', { count: n }),
  };
}

/** Sorted rows for the "On your screens right now" grid. */
export function buildOverviewScreenRows(
  screens: Screen[],
  assignments: ReadonlyMap<string, string | null>,
  playlistNames: ReadonlyMap<string, string>,
): OverviewScreenRow[] {
  return screens
    .map((screen) => {
      const connection = getCardConnectionStatus(screen);
      const playlistId = assignments.get(screen.id) ?? null;
      const playlistName = playlistId ? (playlistNames.get(playlistId) ?? null) : null;
      const hasContent = screenHasSomethingToShow(
        connection,
        playlistId,
        screen.desiredPlaylistVersion,
      );
      return {
        screen,
        connection,
        statusLabel: statusLabelForConnection(connection),
        showingLabel: showingLabelForScreen({ connection, playlistName, hasContent }),
        playlistName,
        hasContent,
        sortRank: CONNECTION_SORT[connection],
      };
    })
    .sort((a, b) => a.sortRank - b.sortRank || a.screen.name.localeCompare(b.screen.name));
}
